/**
 * What the loaded scenario is a case of, shown on hover: the situation, what the protocol
 * prescribes, the tools the run is expected to call, and the outcome of the protocol it tests.
 */

import { FlaskConical, Hammer, Loader2, Route, Target } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { branchTrail } from '@/modules/back-testing/types';
import type { ScenarioTypeBranch, ScenarioTypeTool } from '@/services/ai-function.service';
import { useScenarioType } from './use-scenario-type';

/** Past this many, the tool list is the tooltip rather than a summary of it. */
const TOOLS_SHOWN = 5;

const Line = ({ icon: Icon, label, children }: { icon: typeof Target; label: string; children: React.ReactNode }) => (
  <div className='flex flex-col gap-0.5'>
    <span className='flex items-center gap-1 text-[10px] font-medium tracking-wide text-gray-800 uppercase'>
      <Icon className='size-3' aria-hidden />
      {label}
    </span>
    <div className='text-[11px] leading-4 text-text-body'>{children}</div>
  </div>
);

interface ScenarioTypeLinesProps {
  description: string;
  outcome: string;
  tools: ScenarioTypeTool[];
  /** Where the type sits in its protocol; a type written from a description has no protocol. */
  branch?: ScenarioTypeBranch;
}

/** The type itself, as the peek lays it out: for a stored type and for one written on the spot alike. */
export const ScenarioTypeLines = ({ description, outcome, tools, branch }: ScenarioTypeLinesProps) => {
  const hiddenTools = Math.max(0, tools.length - TOOLS_SHOWN);

  return (
    <div className='flex w-72 flex-col gap-2.5 text-left'>
      <Line icon={FlaskConical} label='Situation'>
        {description}
      </Line>

      <Line icon={Target} label='The protocol says to'>
        {outcome}
      </Line>

      {tools.length > 0 && (
        <Line icon={Hammer} label={`Tools · ${tools.length}`}>
          <span className='flex flex-wrap gap-1'>
            {tools.slice(0, TOOLS_SHOWN).map((tool) => (
              <span
                key={tool.name}
                title={tool.expect || undefined}
                className='rounded-full border border-gray-500 bg-white px-1.5 py-px font-mono text-[10px] text-text-body'
              >
                {tool.name}
              </span>
            ))}
            {hiddenTools > 0 && <span className='text-[10px] text-gray-800'>+{hiddenTools} more</span>}
          </span>
        </Line>
      )}

      {branch && (
        <Line icon={Route} label='In the protocol'>
          <span className='line-clamp-3 text-gray-800'>{branchTrail(branch)}</span>
        </Line>
      )}
    </div>
  );
};

export const ScenarioTypePeek = ({ sopId, scenarioTypeId }: { sopId: string; scenarioTypeId: string }) => {
  const { type, isPending, isError, missing } = useScenarioType(sopId, scenarioTypeId);

  if (isPending) {
    return (
      <div className='flex w-64 flex-col gap-2'>
        <span className='flex items-center gap-1.5 text-[11px] text-gray-800'>
          <Loader2 className='size-3 animate-spin' aria-hidden />
          Reading the scenario type…
        </span>
        <Skeleton className='h-2.5 w-full rounded-full' />
        <Skeleton className='h-2.5 w-3/4 rounded-full' />
      </div>
    );
  }

  // Both are answers, not failures to render.
  if (isError) return <p className='w-56 text-[11px] leading-4'>The scenario type could not be loaded.</p>;
  if (missing || !type)
    return <p className='w-56 text-[11px] leading-4'>This scenario’s type no longer exists under its protocol.</p>;

  return <ScenarioTypeLines description={type.description} outcome={type.outcome} tools={type.tools} branch={type.branch} />;
};
